/**
 * Stakeholder Pulse Page
 *
 * Facilitator view: review generated pulses by priority list, send them to
 * submitters, and monitor the feedback quality signal over time.
 *
 * Route: /prioritization/pulse
 */

import { useEffect, useState, useMemo } from 'react';
import {
  Radio,
  Send,
  CheckCircle2,
  Clock,
  Eye,
  MessageSquare,
  BarChart2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  TrendingUp,
  Loader2,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useStakeholderPulseStore } from '../../stores/prioritization/stakeholderPulseStore';
import { usePrioritySetStore } from '../../stores/prioritization/prioritySetStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useSystemUsers } from '../../hooks/useSystemUsers';
import {
  PULSE_OUTCOME_LABELS,
  PULSE_RESPONSE_LABELS,
  type StakeholderPulse,
  type PulseOutcome,
} from '../../types/prioritization/stakeholderPulse';
import { getCurrentWorkspaceId, getSafeSupabaseClient } from '../../lib/supabaseSync';

// ── Generation stats (read from STAKEHOLDER_PULSE_GENERATED governance events) ─

interface GenerationStats {
  generated: number;
  skipped: number;
  errors: string[];
  occurredAt: string;
}

async function fetchGenerationStats(
  prioritySetIds: string[],
): Promise<Map<string, GenerationStats>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase || !prioritySetIds.length) return new Map();

  const { data, error } = await supabase
    .from('governance_events')
    .select('priority_set_id, after_state_snapshot, occurred_at')
    .eq('event_type', 'STAKEHOLDER_PULSE_GENERATED')
    .in('priority_set_id', prioritySetIds)
    .order('occurred_at', { ascending: false });

  if (error || !data) return new Map();

  // Keep only the most recent event per priority list
  const map = new Map<string, GenerationStats>();
  for (const row of data) {
    if (!row.priority_set_id || map.has(row.priority_set_id)) continue;
    const snap = (row.after_state_snapshot ?? {}) as Record<string, unknown>;
    map.set(row.priority_set_id, {
      generated: Number(snap.generated ?? 0),
      skipped: Number(snap.skipped ?? 0),
      errors: (snap.errors as string[]) ?? [],
      occurredAt: row.occurred_at as string,
    });
  }
  return map;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function outcomeColor(outcome: PulseOutcome): string {
  switch (outcome) {
    case 'above_cut_line': return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
    case 'below_cut_line': return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
    case 'not_in_list':    return 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
  }
}

function statusIcon(status: StakeholderPulse['status']) {
  switch (status) {
    case 'pending':   return <Clock className="h-3.5 w-3.5 text-[var(--text-ter)]" />;
    case 'sent':      return <Send className="h-3.5 w-3.5 text-[var(--text-ter)]" />;
    case 'viewed':    return <Eye className="h-3.5 w-3.5 text-[var(--text-ter)]" />;
    case 'responded': return <MessageSquare className="h-3.5 w-3.5 text-[var(--green)]" />;
  }
}

function responseColor(response: StakeholderPulse['response']): string {
  switch (response) {
    case 'addressed':           return 'text-[var(--green)] dark:text-[var(--text-ter)]';
    case 'partially_addressed': return 'text-[var(--mustard)]';
    case 'not_addressed':       return 'text-[var(--coral)]';
    default:                    return 'text-[var(--text-ter)]';
  }
}

// ── Quality score bar ─────────────────────────────────────────────────────────

function QualityBar({ score, label }: { score: number; label?: string }) {
  const pct = Math.round(score * 100);
  const color =
    pct >= 70 ? 'bg-[var(--green)]' :
    pct >= 40 ? 'bg-[var(--mustard)]'   :
                'bg-[var(--coral)]';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-[var(--text-sec)] w-8 text-right">{pct}%</span>
      {label && <span className="text-xs text-[var(--text-ter)]">{label}</span>}
    </div>
  );
}

// ── Priority list group ────────────────────────────────────────────────────────

function PrioritySetGroup({
  prioritySetId,
  pulses,
  prioritySetName,
  generationStats,
  onSend,
  isSending,
}: {
  prioritySetId: string;
  pulses: StakeholderPulse[];
  prioritySetName: string;
  generationStats?: GenerationStats;
  onSend: (ids: string[]) => void;
  isSending: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const { getSignalForPrioritySet } = useStakeholderPulseStore();
  const { items } = useItemsStore();
  const { users } = useSystemUsers({ workspaceId: getCurrentWorkspaceId() });

  const signal = getSignalForPrioritySet(prioritySetId);
  const pending = pulses.filter(p => p.status === 'pending');

  const itemMap = useMemo(
    () => new Map(items.map(i => [i.id, i.title as string])),
    [items],
  );

  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users ?? []) {
      m.set(u.id, (u.name ?? u.email ?? u.id) as string);
    }
    return m;
  }, [users]);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[var(--surface)] transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            : <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--text-pri)] truncate">{prioritySetName}</p>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              {signal.total} pulse{signal.total !== 1 ? 's' : ''} · {signal.sent} sent · {signal.responded} responded
            </p>
            {/* Generation trace — makes the write path observable per §2.4 */}
            {generationStats ? (
              <p className={`text-xs mt-0.5 ${generationStats.errors.length > 0 ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
                Generated {generationStats.generated} pulse{generationStats.generated !== 1 ? 's' : ''}
                {generationStats.skipped > 0 && `, ${generationStats.skipped} skipped (no submitter)`}
                {generationStats.errors.length > 0 && ` · ${generationStats.errors.length} error${generationStats.errors.length !== 1 ? 's' : ''}`}
                {' · '}{new Date(generationStats.occurredAt).toLocaleDateString()}
              </p>
            ) : (
              <p className="text-xs text-[var(--mustard)] mt-0.5">
                No generation record found — pulses may not have been generated for this list
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
          {signal.responded > 0 && (
            <div className="w-32">
              <QualityBar score={signal.qualityScore} label="quality" />
            </div>
          )}
          {pending.length > 0 && (
            <button
              onClick={e => { e.stopPropagation(); onSend(pending.map(p => p.id)); }}
              disabled={isSending}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white text-xs font-medium transition-colors"
            >
              {isSending
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Send className="h-3.5 w-3.5" />}
              Send {pending.length} pending
            </button>
          )}
        </div>
      </div>

      {/* Pulse list */}
      {expanded && (
        <div className="border-t border-[var(--rule)] divide-y divide-[var(--rule)]">
          {pulses.map(pulse => (
            <div key={pulse.id} className="flex items-center gap-4 px-5 py-3">
              <div className="flex-shrink-0">{statusIcon(pulse.status)}</div>

              <div className="flex-1 min-w-0">
                <p className="text-sm text-[var(--text-pri)] truncate font-medium">
                  {itemMap.get(pulse.itemId) ?? pulse.itemId}
                </p>
                <p className="text-xs text-[var(--text-ter)] truncate">
                  {pulse.submitterId ? userMap.get(pulse.submitterId) ?? pulse.submitterId : 'No submitter'}
                </p>
              </div>

              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${outcomeColor(pulse.outcome)}`}>
                {PULSE_OUTCOME_LABELS[pulse.outcome]}
                {pulse.rank != null && ` #${pulse.rank}`}
              </span>

              {pulse.response ? (
                <span className={`text-xs font-medium ${responseColor(pulse.response)}`}>
                  {PULSE_RESPONSE_LABELS[pulse.response]}
                </span>
              ) : (
                <span className="text-xs text-[var(--text-ter)] italic w-28 text-right">
                  {pulse.status === 'pending' ? 'Not sent' :
                   pulse.status === 'sent'    ? 'Awaiting response' :
                   pulse.status === 'viewed'  ? 'Viewed, no response' :
                   ''}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Signal summary bar ────────────────────────────────────────────────────────

function GlobalSignalBar({ pulses }: { pulses: StakeholderPulse[] }) {
  const total = pulses.length;
  const sent = pulses.filter(p => p.status !== 'pending').length;
  const responded = pulses.filter(p => p.status === 'responded').length;
  const addressed = pulses.filter(p => p.response === 'addressed').length;
  const partial = pulses.filter(p => p.response === 'partially_addressed').length;

  const qualityScore = responded > 0
    ? (addressed * 1 + partial * 0.5) / responded
    : 0;

  const stats = [
    { label: 'Total', value: total, color: 'text-[var(--text-sec)]' },
    { label: 'Sent', value: sent, color: 'text-[var(--text-ter)]' },
    { label: 'Responded', value: responded, color: 'text-[var(--text-ter)]' },
    { label: 'Addressed', value: addressed, color: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  ];

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-4 w-4 text-primary-600" />
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">Feedback Quality Signal</h3>
      </div>
      <div className="grid grid-cols-4 gap-4 mb-4">
        {stats.map(s => (
          <div key={s.label} className="text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>
      {total > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-[var(--text-sec)]">Overall quality score</span>
          </div>
          <QualityBar score={qualityScore} />
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Weighted: Addressed = 1.0 · Partially = 0.5 · Not addressed = 0
          </p>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function StakeholderPulsePage() {
  const {
    pulses,
    isLoading,
    error,
    fetchForWorkspace,
    markSent,
  } = useStakeholderPulseStore();

  const { prioritySets } = usePrioritySetStore();

  const [sendingSetId, setSendingSetId] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [generationStatsMap, setGenerationStatsMap] = useState<Map<string, GenerationStats>>(new Map());

  useEffect(() => {
    fetchForWorkspace();
  }, [fetchForWorkspace]);

  // Group pulses by priority list — must be declared before any early return
  const grouped = useMemo(() => {
    const map = new Map<string, StakeholderPulse[]>();
    for (const pulse of pulses) {
      const arr = map.get(pulse.prioritySetId) ?? [];
      arr.push(pulse);
      map.set(pulse.prioritySetId, arr);
    }
    return map;
  }, [pulses]);

  // Load generation stats from governance_events whenever the set of priority
  // set IDs changes — this is what makes the write path observable.
  useEffect(() => {
    const ids = Array.from(grouped.keys());
    if (!ids.length) return;
    fetchGenerationStats(ids).then(setGenerationStatsMap);
  }, [grouped]);

  const prioritySetName = (id: string) =>
    prioritySets.find(ps => ps.id === id)?.name ?? 'Priority List';

  const handleSend = async (prioritySetId: string, pulseIds: string[]) => {
    setSendingSetId(prioritySetId);
    const { sentCount, error: sendErr } = await markSent(pulseIds);
    setSendingSetId(null);
    if (!sendErr) {
      setSendSuccess(`${sentCount} pulse${sentCount !== 1 ? 's' : ''} sent — submitters notified in-app.`);
      setTimeout(() => setSendSuccess(null), 4000);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        icon={<Radio className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Stakeholder Pulse"
        subtitle="Close the loop — send outcome summaries to idea submitters and track their responses."
        actions={
          <button
            onClick={() => fetchForWorkspace()}
            className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] px-3 py-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] transition-colors"
          >
            Refresh
          </button>
        }
      />

      {/* Toast */}
      {sendSuccess && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          <CheckCircle2 className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
          {sendSuccess}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
          <AlertCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0" />
          {error}
        </div>
      )}

      {isLoading && pulses.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Loading pulses…</span>
        </div>
      ) : pulses.length === 0 ? (
        <div className="text-center py-16 text-[var(--text-ter)]">
          <Radio className="h-8 w-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium text-[var(--text-ter)]">No pulses yet</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">
            Pulses are generated automatically when a Priority List is published.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Signal summary */}
          <GlobalSignalBar pulses={pulses} />

          {/* Per-priority-list groups */}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--text-sec)]">By Priority List</h2>
            <div className="flex items-center gap-3 text-xs text-[var(--text-ter)]">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Pending</span>
              <span className="flex items-center gap-1"><Send className="h-3 w-3 text-[var(--text-ter)]" /> Sent</span>
              <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-[var(--text-ter)]" /> Viewed</span>
              <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3 text-[var(--green)]" /> Responded</span>
            </div>
          </div>

          {Array.from(grouped.entries()).map(([psId, psPulses]) => (
            <PrioritySetGroup
              key={psId}
              prioritySetId={psId}
              pulses={psPulses}
              prioritySetName={prioritySetName(psId)}
              generationStats={generationStatsMap.get(psId)}
              onSend={(ids) => handleSend(psId, ids)}
              isSending={sendingSetId === psId}
            />
          ))}
        </div>
      )}

      {/* How it works info */}
      <div className="bg-[var(--surface)] border border-[var(--rule)] p-5">
        <div className="flex items-start gap-3">
          <BarChart2 className="h-4 w-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-[var(--text-sec)] mb-1">How Stakeholder Pulse works</p>
            <ul className="text-xs text-[var(--text-ter)] space-y-1 list-disc list-inside">
              <li>When a Priority List is published, a pulse is generated for each item with a submitter.</li>
              <li>Click <strong>Send pending</strong> to deliver outcome summaries to submitters.</li>
              <li>Submitters respond in their <strong>My Submissions</strong> view: "Did we address your concern?"</li>
              <li>The <strong>quality score</strong> reflects how well submitters feel their concerns were addressed over time.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
